import { useMutation } from '@tanstack/react-query';
import { Smartphone } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';

interface EnrolmentResult {
  code: string;
  expiresAt: string;
  driverName: string;
}

const DRIVER_APP_URL = import.meta.env.VITE_DRIVER_APP_URL ?? 'https://kmdx-driver.ovyra.ai';

/**
 * Код за свързване на телефона на шофьора. Кодът е еднократен: ако шофьорът
 * смени телефона или браузъра, или отвори приложението на нов адрес, трябва
 * нов код — и офисът вече може да го издаде сам, без програмист.
 */
export function PhoneCodeButton({ driverId, driverName }: { driverId: string; driverName: string }) {
  const issue = useMutation({
    mutationFn: () =>
      api.post<EnrolmentResult>('/auth/driver/enrolment', { driverId, deviceLabel: 'Телефон на шофьора' }),
    meta: { silent: true },
  });
  const code = issue.data?.code;
  const pretty = code && code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
  const until = issue.data
    ? new Intl.DateTimeFormat('bg-BG', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(issue.data.expiresAt))
    : '';

  return (
    <>
      <Button size="sm" variant="ghost" icon={<Smartphone className="size-4" />} onClick={() => issue.mutate()}>
        Код за телефона
      </Button>
      <Dialog
        open={issue.isSuccess || issue.isError}
        onClose={() => issue.reset()}
        title={`Код за телефона — ${driverName}`}
        footer={<Button onClick={() => issue.reset()}>Готово</Button>}
      >
        {issue.isError ? (
          <p className="text-sm text-danger">
            {issue.error instanceof ApiError ? issue.error.message : 'Кодът не беше издаден. Опитайте пак.'}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-line bg-raised p-4 text-center">
              <div className="font-mono text-3xl font-bold tracking-[0.2em]">{pretty}</div>
              <div className="mt-1 text-xs text-ink-dim">важи до {until} · еднократен</div>
            </div>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-dim">
              <li>
                Шофьорът отваря на телефона <span className="font-mono text-ink">{DRIVER_APP_URL.replace('https://', '')}</span>
              </li>
              <li>Въвежда кода и си избира 4-цифрен ПИН.</li>
              <li>Оттук нататък влиза само с ПИН-а. Нов код трябва само при нов телефон или изтрити данни.</li>
            </ol>
            <p className="text-xs text-ink-faint">Старият код на този шофьор, ако има неизползван, вече не важи.</p>
          </div>
        )}
      </Dialog>
    </>
  );
}
